import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (message, type = "info", options = {}) => {
      const id = Date.now() + Math.random();
      const duration = options.duration ?? 4000;
      const persistent = options.persistent ?? false;
      const pauseOnHover = options.pauseOnHover ?? true;

      const toast = {
        id,
        message,
        type,
        duration,
        persistent,
        pauseOnHover,
        ...options,
      };

      setToasts((prev) => [...prev, toast]);

      if (!persistent && duration > 0 && !pauseOnHover) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }

      return id;
    },
    [removeToast]
  );

  const removeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const success = useCallback(
    (message, options) => addToast(message, "success", options),
    [addToast]
  );
  const error = useCallback(
    (message, options) => addToast(message, "error", options),
    [addToast]
  );
  const warning = useCallback(
    (message, options) => addToast(message, "warning", options),
    [addToast]
  );
  const info = useCallback(
    (message, options) => addToast(message, "info", options),
    [addToast]
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__currentToast = { success, error, warning, info };
    }
    return () => {
      if (typeof window !== "undefined" && window.__currentToast?.error === error) {
        delete window.__currentToast;
      }
    };
  }, [success, error, warning, info]);

  const value = {
    toasts,
    addToast,
    removeToast,
    removeAllToasts,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
};
